import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useFormContext } from "react-hook-form";
import { Contact, Title } from "@/types"; // Import the Title enum
import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { getFieldError } from "@/utils";

interface AdditionalContactsStepProps {
  additionalContacts: number;
  onAdditionalContactsChange: (value: number) => void;
  isExplorer?: boolean;
}

export function AdditionalContactsStep({ additionalContacts, onAdditionalContactsChange, isExplorer = false }: AdditionalContactsStepProps) {
  const { setValue, getValues, watch, formState: { errors }, register } = useFormContext();
  const additionalContactsValues = watch('additionalContacts') || [];
  const ownerData = watch('owner');

  // Initialize additionalContacts array if it doesn't exist
  const initializeAdditionalContacts = (count: number) => {
    const currentContacts = getValues('additionalContacts') || [];

    // Create an array of the specified length with empty contact objects
    const newContacts: Contact[] = Array(count).fill(null).map((_, index) => {
      // Preserve existing contact data if available
      return currentContacts[index] || {
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        isJointMember: false
      };
    });

    // Set the additionalContacts array in the form
    setValue('additionalContacts', newContacts);
  };

  // Update additionalContacts array when count changes
  const handleContactCountChange = (count: number) => {
    onAdditionalContactsChange(count);
    initializeAdditionalContacts(count);
  };

  // Ensure all existing contacts have their titles set
  useEffect(() => {
    if (additionalContactsValues.length > 0) {
      additionalContactsValues.forEach((contact: Contact, index: number) => {
        if (contact && contact.title) {
          setValue(`additionalContacts.${index}.title`, contact.title);
        }
        // Ensure isJointMember is seeded from existing data on edit
        if (contact && typeof contact.isJointMember !== "undefined") {
          setValue(`additionalContacts.${index}.isJointMember`, Boolean(contact.isJointMember));
        }
      });
    }
  }, [additionalContactsValues, setValue]);

  return (
    <div className="space-y-6">
      {/* Dropdown to select the number of additional contacts */}
      <div className="space-y-2">
        <Label>How many additional contacts would you like to add?</Label>
        <Select
          value={additionalContacts.toString()}
          onValueChange={(value) => handleContactCountChange(parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select number of additional contacts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">No additional contacts</SelectItem>
            <SelectItem value="1">1 additional contact</SelectItem>
            {!isExplorer && (
              <>
                <SelectItem value="2">2 additional contacts</SelectItem>
                <SelectItem value="3">3 additional contacts</SelectItem>
                <SelectItem value="4">4 additional contacts</SelectItem>
                <SelectItem value="5">5 additional contacts</SelectItem>
                <SelectItem value="6">6 additional contacts</SelectItem>
                <SelectItem value="7">7 additional contacts</SelectItem>
                <SelectItem value="8">8 additional contacts</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Render ContactFormItem for each additional contact */}
      {Array.from({ length: additionalContacts }).map((_, index) => (
        <div key={index} className="space-y-4 pt-4 border-t">
          <h4 className="font-medium">Additional Contact #{index + 1}</h4>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-[80px]">
                <Label htmlFor={`additionalContacts.${index}.title`}>Title *</Label>
                <Select
                  value={additionalContactsValues[index]?.title || ""}
                  onValueChange={(value) => setValue(`additionalContacts.${index}.title`, value, { shouldValidate: true })}
                >
                  <SelectTrigger
                    className={`w-full h-10 ${getFieldError(errors, `additionalContacts.${index}.title`) ? "border-destructive ring-destructive" : ""}`}
                  >
                    <SelectValue placeholder="Title" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Use the Title enum instead of hardcoded values */}
                    {Object.values(Title).map((title) => (
                      <SelectItem key={title} value={title}>{title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  {...register(`additionalContacts.${index}.title`, { required: true })}
                  type="hidden"
                />
              </div>

              <div className="flex-1 min-w-[120px]">
                <Label htmlFor={`additionalContacts.${index}.firstName`}>First Name *</Label>
                <Input
                  id={`additionalContacts.${index}.firstName`}
                  {...register(`additionalContacts.${index}.firstName`, { required: true })}
                  className={getFieldError(errors, `additionalContacts.${index}.firstName`) ? "border-destructive ring-destructive" : ""}
                />
              </div>

              <div className="flex-1 min-w-[120px]">
                <Label htmlFor={`additionalContacts.${index}.middleName`}>Middle Name</Label>
                <Input
                  id={`additionalContacts.${index}.middleName`}
                  {...register(`additionalContacts.${index}.middleName`)}
                />
              </div>

              <div className="flex-1 min-w-[120px]">
                <Label htmlFor={`additionalContacts.${index}.lastName`}>Last Name *</Label>
                <Input
                  id={`additionalContacts.${index}.lastName`}
                  {...register(`additionalContacts.${index}.lastName`, { required: true })}
                  className={getFieldError(errors, `additionalContacts.${index}.lastName`) ? "border-destructive ring-destructive" : ""}
                />
              </div>
            </div>

            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor={`additionalContacts.${index}.email`}>Email *</Label>
                <Input
                  id={`additionalContacts.${index}.email`}
                  type="email"
                  {...register(`additionalContacts.${index}.email`, {
                    required: true,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                  className={getFieldError(errors, `additionalContacts.${index}.email`) ? "border-destructive ring-destructive" : ""}
                />
              </div>
              <div className="flex items-center space-x-2 pb-1">
                <Checkbox
                  id={`additionalContacts.${index}.isJointMember`}
                  checked={additionalContactsValues[index]?.isJointMember || false}
                  disabled={isExplorer}
                  onCheckedChange={(checked) => {
                    if (isExplorer) return; // No joint members for Explorer
                    // Count joint members excluding the current contact
                    const currentIsJoint = additionalContactsValues[index]?.isJointMember || false;
                    const otherJointMembers = (ownerData?.isJointMember ? 1 : 0) + 
                      additionalContactsValues.filter((contact: any, idx: number) => idx !== index && contact?.isJointMember).length;
                    
                    // Prevent checking if it would exceed the limit
                    if (checked && !currentIsJoint && otherJointMembers >= 4) {
                      return;
                    }
                    setValue(`additionalContacts.${index}.isJointMember`, checked === true, { shouldDirty: true });
                  }}
                />
                {!isExplorer && (
                  <Label htmlFor={`additionalContacts.${index}.isJointMember`} className="text-sm font-normal">
                    Joint Member
                  </Label>
                )}
                {isExplorer && (
                  <Label htmlFor={`additionalContacts.${index}.isJointMember`} className="text-sm font-normal text-muted-foreground">
                    Joint Member (N/A for Explorer)
                  </Label>
                )}
                {/* Register the field so RHF tracks default/reset values */}
                <input type="hidden" {...register(`additionalContacts.${index}.isJointMember`)} />
              </div>
              {(() => {
                const currentIsJoint = additionalContactsValues[index]?.isJointMember || false;
                const otherJointMembers = (ownerData?.isJointMember ? 1 : 0) + 
                  additionalContactsValues.filter((contact: any, idx: number) => idx !== index && contact?.isJointMember).length;
                return !currentIsJoint && otherJointMembers >= 4 ? (
                  <p className="text-xs text-amber-600 mt-1">
                    Maximum of 4 joint members allowed
                  </p>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}